import { Injectable } from '@angular/core';

import {
	CreateCustomPropertyRequest,
	GetCustomPropertiesRequest,
	UpdateCustomPropertyRequest,
} from '@reach/interfaces/custom-property/custom-properties.requests';
import {
	CreateCustomPropertyResponse,
	DeleteCustomPropertyResponse,
	GetCustomPropertiesResponse,
	MergePropertyResponse,
	RemovePropertyValueFromNodesResponse,
	UpdateCustomPropertyResponse,
	UpdatePropertyOptionsResponse,
	UpdatePropertyVisibilityValueToNodesResponse,
	UpsertPropertyValueToNodesResponse,
} from '@reach/interfaces/custom-property/custom-properties.responses';
import { CustomPropertySelectionOption } from '@reach/interfaces/custom-property/custom-property.interface';
import {
	CustomPropertyValueUpdateVisibilityInterface,
	CustomPropertyValueUpsertInterface,
} from '@reach/interfaces/custom-property/custom-property-value.interface';

import { BaseApiService } from '~app-client/api/_internal';
import { ReachNodeId } from '~app-client/core/types';

@Injectable({ providedIn: 'root' })
export class PropertiesApiService extends BaseApiService {
	protected commonPath = ['users', 'me', 'custom-properties'];

	public async createCustomProperty(
		body: CreateCustomPropertyRequest
	): Promise<CreateCustomPropertyResponse> {
		return await this.http
			.post<CreateCustomPropertyResponse>(this.url(), body)
			.then((response) => {
				return Array.isArray(response) ? response[0] : response;
			});
	}

	public async updatePropertyOptions(
		propertyId: string,
		options: CustomPropertySelectionOption[]
	): Promise<UpdatePropertyOptionsResponse> {
		const body = {
			options: options
				.filter((option) => option?.value?.trim())
				.map((option) => {
					const result: CustomPropertySelectionOption = { value: option.value.trim() };
					if (option.color) {
						result.color = option.color;
					}
					return result;
				}),
		};

		return await this.http.post<UpdatePropertyOptionsResponse>(
			this.url(propertyId, 'options'),
			body
		);
	}

	public async upsertPropertyToNode(
		nodeIds: ReachNodeId[],
		customProperty: CustomPropertyValueUpsertInterface
	): Promise<UpsertPropertyValueToNodesResponse> {
		const body = { nodeIds, ...customProperty };
		return await this.http.post<UpsertPropertyValueToNodesResponse>(
			this.url('value/upsert'),
			body
		);
	}

	public async updateVisibilityValueToNode(
		nodeIds: ReachNodeId[],
		customProperty: CustomPropertyValueUpdateVisibilityInterface
	): Promise<UpdatePropertyVisibilityValueToNodesResponse> {
		const body = { nodeIds, ...customProperty };
		return await this.http.post<UpdatePropertyVisibilityValueToNodesResponse>(
			this.url('value/visibility'),
			body
		);
	}

	public async removePropertyFromNode(
		nodeIds: ReachNodeId[],
		customPropertyId: string
	): Promise<RemovePropertyValueFromNodesResponse> {
		const body = { nodeIds, customPropertyId };
		return await this.http.post<RemovePropertyValueFromNodesResponse>(
			this.url('/value/remove'),
			body
		);
	}

	public async getUserProperties(): Promise<GetCustomPropertiesResponse> {
		return await this.http.post<GetCustomPropertiesResponse>(this.url('get-custom-properties'));
	}

	public async getWorkspacesUserProperties(
		workspaceIds: ReachNodeId[],
		includeUnassigned: boolean,
		includeTrash: boolean
	): Promise<GetCustomPropertiesResponse> {
		const body: GetCustomPropertiesRequest = {
			workspaceIds: workspaceIds,
			includeUnassignedNodes: includeUnassigned,
			includeTrashNodes: includeTrash,
		};
		return await this.http.post<GetCustomPropertiesResponse>(
			this.url('get-custom-properties'),
			body
		);
	}

	public async deleteProperty(propertyId: string): Promise<DeleteCustomPropertyResponse> {
		return await this.http.delete<DeleteCustomPropertyResponse>(this.url(propertyId));
	}

	public async updateProperty(
		body: UpdateCustomPropertyRequest
	): Promise<UpdateCustomPropertyResponse> {
		return await this.http.patch<UpdateCustomPropertyResponse>(
			this.url(body.customPropertyId),
			body
		);
	}

	public async mergeProperties(
		targetPropertyId: string,
		otherPropertyIds: string[]
	): Promise<MergePropertyResponse> {
		const body = { targetPropertyId, otherPropertyIds };
		return await this.http.post<MergePropertyResponse>(this.url('merge'), body);
	}
}
